import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { Button } from "../../UI/MUIButton";
import { EntrataUser } from "../../services/IntegrationApi";
import { Portfolio, PossibleMigration } from "../../services/PortfolioApi";
import { LegacyFieldValue } from "./useImportMigrationWizard";
import { MigrationFlowKind } from "./types";
import { LegacyFields } from "./LegacyFields";

interface ConfigureStepProps {
  targetOptions: string[];
  targetType: string;
  onTargetChange: (value: string) => void;

  flowKind: MigrationFlowKind;
  selectedMigration: PossibleMigration | undefined;

  // new-flow fields
  portfolios: Portfolio[];
  entrataUsers: EntrataUser[];
  portfolioId: string;
  onPortfolioChange: (value: string) => void;
  entrataUserId: number | "";
  onEntrataUserChange: (value: number) => void;
  entrataPropertyId: string;
  onEntrataPropertyIdChange: (value: string) => void;
  downloadingTemplate: boolean;
  onDownloadTemplate: () => void;

  // legacy fields
  legacyFields: Record<string, LegacyFieldValue>;
  onLegacyFieldChange: (name: string, value: LegacyFieldValue) => void;
}

export const ConfigureStep: React.FC<ConfigureStepProps> = ({
  targetOptions,
  targetType,
  onTargetChange,
  flowKind,
  selectedMigration,
  portfolios,
  entrataUsers,
  portfolioId,
  onPortfolioChange,
  entrataUserId,
  onEntrataUserChange,
  entrataPropertyId,
  onEntrataPropertyIdChange,
  downloadingTemplate,
  onDownloadTemplate,
  legacyFields,
  onLegacyFieldChange,
}) => (
  <Grid container spacing={2.5} direction="column">
    <Grid item>
      <TextField
        select
        fullWidth
        label="Migrate to"
        value={targetType}
        onChange={(e) => onTargetChange(String(e.target.value))}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        disabled={targetOptions.length === 0}
      >
        <MenuItem value="" disabled>
          <Box component="span" color="text.disabled">
            {targetOptions.length === 0 ? "No migration targets available" : "Select target..."}
          </Box>
        </MenuItem>
        {targetOptions.map((target) => (
          <MenuItem key={target} value={target}>{target}</MenuItem>
        ))}
      </TextField>
    </Grid>

    {flowKind === "entrata" && (
      <>
        <Grid item>
          <TextField
            select
            fullWidth
            label="New Portfolio"
            value={portfolioId}
            onChange={(e) => onPortfolioChange(String(e.target.value))}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="" disabled>
              <Box component="span" color="text.disabled">Select portfolio...</Box>
            </MenuItem>
            {portfolios.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item>
          <TextField
            select
            fullWidth
            label="Entrata Connection"
            value={entrataUserId === "" ? "" : String(entrataUserId)}
            onChange={(e) => onEntrataUserChange(Number(e.target.value))}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="" disabled>
              <Box component="span" color="text.disabled">Select connection...</Box>
            </MenuItem>
            {entrataUsers.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.subDomain} ({u.userName})
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item>
          <TextField
            fullWidth
            label="Entrata Property ID"
            placeholder="Enter value..."
            type="number"
            value={entrataPropertyId}
            onChange={(e) => onEntrataPropertyIdChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item>
          <Divider />
        </Grid>

        <Grid item>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<FileDownloadOutlinedIcon />}
            loading={downloadingTemplate}
            onClick={onDownloadTemplate}
          >
            Download CSV Template
          </Button>
        </Grid>

        <Grid item>
          <Typography variant="body2" color="text.secondary">
            Fill in the Entrata IDs in the downloaded template, then upload it in the next step.
          </Typography>
        </Grid>
      </>
    )}

    {flowKind === "legacy" && targetType && selectedMigration && (
      <LegacyFields
        fields={selectedMigration.fields ?? []}
        values={legacyFields}
        onChange={onLegacyFieldChange}
      />
    )}
  </Grid>
);
